import React, { useState, useMemo } from 'react';
import { useSuppliers } from '../hooks/useSuppliers';
import SupplierForm from '../components/suppliers/SupplierForm';
import SupplierList from '../components/suppliers/SupplierList';
import { Supplier, CreateSupplierFormData } from '../types/supplier';
import {
  PlusIcon,
  TruckIcon
} from '@heroicons/react/24/outline';
import SearchBar from '../components/common/SearchBar';
import LoadingScreen from '../components/common/LoadingScreen';
import toast from 'react-hot-toast';
import Pagination from 'components/common/Pagination';
import ConfirmDeleteModal from 'components/common/ConfirmDeleteModal';
import CommonPageHeader from '../components/common/CommonPageHeader';

const Suppliers: React.FC = () => {
  const { suppliers, loading, createSupplier, updateSupplier, deleteSupplier } = useSuppliers();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState<Supplier | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const handleCreateSupplier = async (data: CreateSupplierFormData) => {
    try {
      await createSupplier(data);
      setIsModalOpen(false);
      toast.success('Beszállító sikeresen létrehozva');
    } catch (error) {
      console.error('Error creating supplier:', error);
      toast.error('Hiba a beszállító létrehozása során');
    }
  };

  const handleEditSupplier = async (data: CreateSupplierFormData) => {
    if (!selectedSupplier) return;

    try {
      await updateSupplier(selectedSupplier.id, data);
      setIsModalOpen(false);
      setSelectedSupplier(null);
      toast.success('Beszállító sikeresen frissítve');
    } catch (error) {
      console.error('Error updating supplier:', error);
      toast.error('Hiba a beszállító frissítése során');
    }
  };

  const handleDeleteSupplier = async (supplier: Supplier) => {
    try {
      await deleteSupplier(supplier.id);
      toast.success('Beszállító sikeresen törölve');
    } catch (error) {
      console.error('Error deleting supplier:', error);
      toast.error('Hiba a beszállító törlése során');
    }
  };

  const handleStatusChange = async (supplier: Supplier) => {
    try {
      const newStatus = !supplier.isActive;
      await updateSupplier(supplier.id, {
        ...supplier,
        isActive: newStatus
      });
      toast.success(`Beszállító ${newStatus ? 'aktiválva' : 'deaktiválva'}`);
    } catch (error) {
      console.error('Error updating supplier status:', error);
      toast.error('Hiba a beszállító státusz frissítése során');
    }
  };

  const openEditModal = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setIsModalOpen(true);
  };

  const openDeleteModal = (supplier: Supplier) => {
    setSupplierToDelete(supplier);
    setIsConfirmDeleteOpen(true);
  };

  // Szűrés kezelése
  const filteredSuppliers = useMemo(() => {
    return suppliers.filter(supplier => {
      // Keresés alapján szűrés
      const searchMatch =
        supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        supplier.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        supplier.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        supplier.taxNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        supplier.address?.street?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        supplier.address?.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        supplier.address?.postalCode?.toLowerCase().includes(searchTerm.toLowerCase());

      // Státusz alapján szűrés
      const statusMatch = 
        statusFilter === 'all' ||
        (statusFilter === 'active' && supplier.isActive) ||
        (statusFilter === 'inactive' && !supplier.isActive);

      return searchMatch && statusMatch;
    });
  }, [suppliers, searchTerm, statusFilter]);

  // Pagination logic
  const totalPages = Math.ceil(filteredSuppliers.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentSuppliers = filteredSuppliers.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-4 max-w-[100vw] px-2 sm:px-0 overflow-x-hidden">
      <CommonPageHeader
        title="Beszállítók"
        subtitle="Beszállítók kezelése és áttekintése"
        icon={TruckIcon}
      >
          <button
            onClick={() => {
              setSelectedSupplier(null);
              setIsModalOpen(true);
            }}
          className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors"
          >
          <PlusIcon className="h-4 w-4 mr-2" />
            <span>Új beszállító</span>
          </button>
      </CommonPageHeader>

      {/* Szűrési lehetőségek */}
      <div className="space-y-2 sm:space-y-3">
        <div className="w-full">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Keresés név, email, adószám alapján..."
          />
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto pb-2 scrollbar-none -mx-4 px-4 sm:mx-0 sm:px-0">
          {[
            { key: 'all', label: 'Mind', count: suppliers.length },
            { key: 'active', label: 'Aktív', count: suppliers.filter(s => s.isActive).length },
            { key: 'inactive', label: 'Inaktív', count: suppliers.filter(s => !s.isActive).length }
          ].map(({ key, label, count }) => (
            <button
              key={key}
              onClick={() => setStatusFilter(key as any)}
              className={`flex-none inline-flex items-center px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border text-xs sm:text-sm whitespace-nowrap
                ${statusFilter === key
                  ? 'border-primary-500 dark:border-primary-400 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
            >
              {label} ({count})
            </button>
          ))}
        </div>
      </div>

      <SupplierList
        suppliers={currentSuppliers}
        onStatusChange={handleStatusChange}
        onEditClick={openEditModal}
        onDeleteClick={openDeleteModal}
      />

      {/* Pagination controls */}
      <div className="relative z-10">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          totalItems={filteredSuppliers.length}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          indexOfFirstItem={indexOfFirstItem}
          indexOfLastItem={indexOfLastItem}
        />
      </div>

      {/* Beszállító form modal */}
      {isModalOpen && (
        <SupplierForm
          supplier={selectedSupplier}
          onSubmit={selectedSupplier ? handleEditSupplier : handleCreateSupplier}
          onCancel={() => {
            setIsModalOpen(false);
            setSelectedSupplier(null);
          }}
        />
      )}

      {/* Törlés megerősítő modal */}
      <ConfirmDeleteModal
        isOpen={isConfirmDeleteOpen}
        onClose={() => {
          setIsConfirmDeleteOpen(false);
          setSupplierToDelete(null);
        }}
        onConfirm={() => {
          if (supplierToDelete) {
            handleDeleteSupplier(supplierToDelete);
            setIsConfirmDeleteOpen(false);
            setSupplierToDelete(null);
          }
        }}
        title="Beszállító törlése"
        deleteMessage={`Biztosan törölni szeretnéd a következő beszállítót: ${supplierToDelete?.name}?`}
        deleteWarning="Ez a művelet nem vonható vissza."
      />
    </div>
  );
};

export default Suppliers; 